import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  bulkInviteOrphanedCompaniesService,
  getOrphanedCompaniesService,
  resendCompanyRecoveryService,
} from "../../services/CompanyRecovery/CompanyRecoveryServices";
import { showError, showSuccess } from "../Toast/Toast.functions";
import AdminPageHeader from "./AdminPageHeader";

interface OrphanedCompany {
  _id: string;
  name: string;
  email: string;
  vat: string;
  status: "NEVER_INVITED" | "PENDING" | "EXPIRED" | "CLAIMED";
  recoveryTokenExpiresAt?: string;
  claimedAt?: string;
}

const STATUS_SEVERITY: Record<OrphanedCompany["status"], "info" | "warning" | "danger" | "success"> = {
  NEVER_INVITED: "info",
  PENDING: "warning",
  EXPIRED: "danger",
  CLAIMED: "success",
};

const AdminCompanyRecovery = () => {
  const { t } = useTranslation(["common"]);
  const toast = useRef<Toast>(null);

  const [companies, setCompanies] = useState<OrphanedCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [confirmInviteAll, setConfirmInviteAll] = useState(false);

  const statusLabel = (status: OrphanedCompany["status"]) =>
    t(`common:companyRecoveryStatus_${status}`);

  const loadCompanies = () => {
    setLoading(true);
    getOrphanedCompaniesService()
      .then((res: any) => setCompanies(res.data ?? []))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  const filteredCompanies = companies.filter((c) => {
    const search = globalFilterValue.trim().toLowerCase();
    if (!search) return true;
    return (
      c.name?.toLowerCase().includes(search) ||
      c.vat?.toLowerCase().includes(search) ||
      c.email?.toLowerCase().includes(search)
    );
  });

  const inviteAll = () => {
    setConfirmInviteAll(false);
    bulkInviteOrphanedCompaniesService()
      .then(() => {
        showSuccess(toast, t("common:companyRecoveryInvitesSent"));
        loadCompanies();
      })
      .catch(() => showError(toast, t("common:genericError")));
  };

  const resend = (company: OrphanedCompany) => {
    resendCompanyRecoveryService({ companyId: company._id })
      .then(() => {
        showSuccess(toast, t("common:companyRecoveryInvitesSent"));
        loadCompanies();
      })
      .catch(() => showError(toast, t("common:genericError")));
  };

  return (
    <div className="admin-page">
      <Toast ref={toast} position="top-right" />
      <AdminPageHeader title={t("common:companyRecovery")} />

      <div className="admin-page-body">
        <div className="flex justify-content-space-between align-items-center mb-3">
          <div className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              value={globalFilterValue}
              onChange={(e) => setGlobalFilterValue(e.target.value)}
              placeholder="Keyword Search"
            />
          </div>
          <Button
            className="general-button"
            label={t("common:companyRecoveryInviteAll")}
            onClick={() => setConfirmInviteAll(true)}
          />
        </div>

        <DataTable
          value={filteredCompanies}
          loading={loading}
          paginator
          rows={20}
          dataKey="_id"
          sortField="name"
          sortOrder={1}
          emptyMessage={t("common:noResults")}
        >
          <Column field="name" header={t("common:Name")} sortable />
          <Column field="vat" header={t("common:vat")} sortable />
          <Column field="email" header={t("common:Email")} />
          <Column
            field="status"
            header={t("common:status")}
            body={(row: OrphanedCompany) => (
              <Tag value={statusLabel(row.status)} severity={STATUS_SEVERITY[row.status]} />
            )}
          />
          <Column
            field="recoveryTokenExpiresAt"
            header={t("common:companyRecoveryExpiresAt")}
            body={(row: OrphanedCompany) =>
              row.recoveryTokenExpiresAt ? new Date(row.recoveryTokenExpiresAt).toLocaleDateString() : "-"
            }
          />
          <Column
            body={(row: OrphanedCompany) => (
              <Button
                label={t("common:companyRecoveryResend")}
                className="p-button-text"
                disabled={row.status === "CLAIMED"}
                onClick={() => resend(row)}
              />
            )}
          />
        </DataTable>
      </div>

      <Dialog
        onHide={() => setConfirmInviteAll(false)}
        visible={confirmInviteAll}
        closable={false}
        style={{ maxWidth: "50vh" }}
        footer={
          <div className="flex justify-content-space-between">
            <Button className="general-button" onClick={() => setConfirmInviteAll(false)}>
              {t("common:No")}
            </Button>
            <Button className="general-button" onClick={inviteAll}>
              {t("common:Yes")}
            </Button>
          </div>
        }
      >
        {t("common:companyRecoveryInviteAllConfirm")}
      </Dialog>
    </div>
  );
};

export default AdminCompanyRecovery;
